import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { timelineUseCases } from '@/features/timeline/libs/timeline-services';
import { ViewPreferenceStore } from '@/features/timeline/infrastructure/view-preference-store';
import { TIMELINE_SCALE_LABELS, type TimelineScaleLabel } from '../libs/timeline-config';
import { TimelineHeader } from './components/timeline-header';
import { TimelineCalendar } from './timeline-calendar';
import { useTimelineDetailController } from './use-timeline-detail-controller';

const viewPreferenceStore = new ViewPreferenceStore();

const FullScreenMessage = ({
  title,
  description,
  actionLabel,
  onAction,
}: {
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center"
      style={{ backgroundColor: 'var(--color-bg-app)' }}
    >
      <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
        {title}
      </h1>
      <p className="max-w-md text-sm leading-6" style={{ color: 'var(--color-text-secondary)' }}>
        {description}
      </p>
      <button
        type="button"
        className="rounded-xl px-4 py-3 text-sm font-medium"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-accent-fg)',
        }}
        onClick={onAction}
      >
        {actionLabel}
      </button>
      <Link
        to="/timelines"
        className="text-sm font-medium"
        style={{ color: 'var(--color-accent)' }}
      >
        {t('nav.backToList', { ns: 'common' })}
      </Link>
    </div>
  );
};

const getInitialScaleFromLocationState = (state: unknown): TimelineScaleLabel | undefined => {
  if (!state || typeof state !== 'object') {
    return undefined;
  }

  const initialScale = (state as { initialScale?: unknown }).initialScale;
  return TIMELINE_SCALE_LABELS.includes(initialScale as TimelineScaleLabel)
    ? initialScale as TimelineScaleLabel
    : undefined;
};

export const TimelineDetailPage = () => {
  const { t } = useTranslation('timeline');
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingInitialScale, setPendingInitialScale] = useState<TimelineScaleLabel | undefined>(
    () => getInitialScaleFromLocationState(location.state),
  );
  const [savedViewRange, setSavedViewRange] = useState<{ start: Date; end: Date } | undefined>();
  const [viewPrefLoaded, setViewPrefLoaded] = useState(false);

  useEffect(() => {
    if (!id) {
      setViewPrefLoaded(true);
      return;
    }
    let cancelled = false;
    void viewPreferenceStore.get(id).then((pref) => {
      if (cancelled) return;
      if (pref) {
        setSavedViewRange({
          start: new Date(pref.viewRangeStartMs),
          end: new Date(pref.viewRangeEndMs),
        });
        if (!getInitialScaleFromLocationState(location.state) && TIMELINE_SCALE_LABELS.includes(pref.scale as TimelineScaleLabel)) {
          setPendingInitialScale(pref.scale as TimelineScaleLabel);
        }
      }
      setViewPrefLoaded(true);
    }).catch(() => {
      if (!cancelled) setViewPrefLoaded(true);
    });
    return () => { cancelled = true; };
  }, [id, location.state]);

  const handleViewChange = useCallback((scale: string, viewRange: { start: Date; end: Date }) => {
    if (!id) return;
    void viewPreferenceStore.save({
      timelineId: id,
      scale,
      viewRangeStartMs: viewRange.start.getTime(),
      viewRangeEndMs: viewRange.end.getTime(),
    }).catch(() => {});
  }, [id]);
  const {
    timelineData,
    loading,
    error,
    errorCode,
    isDirty,
    retry,
    fileName,
    handleSave,
    handleSaveAs,
    addTimelineSection,
    handleSectionDelete,
    handleSectionMove,
    handleSectionLabelChange,
    handleSectionVisibilityToggle,
    handleItemTitleChange,
    handleItemCreate,
    handleItemDateRangeChange,
    handleItemDelete,
  } = useTimelineDetailController(id);

  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const leave = window.confirm(
        t('detail.leaveConfirm'),
      );
      if (leave) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  const handleNewFile = useCallback(async () => {
    try {
      const created = await timelineUseCases.createTimeline();
      navigate(`/timeline/${created.id}`, {
        state: { initialScale: 'week' as const },
      });
    } catch {
      console.error(t('error.createFailed'));
    }
  }, [navigate]);

  const handleOpenFile = useCallback(async () => {
    try {
      const result = await timelineUseCases.openFile();
      if (!result) return;
      navigate(`/timeline/${result.record.id}`);
    } catch (err) {
      console.error(t('error.openFailed'), err);
    }
  }, [navigate]);

  if ((loading && !timelineData) || !viewPrefLoaded) {
    return (
      <div
        className="flex h-screen items-center justify-center"
        style={{ backgroundColor: 'var(--color-bg-app)' }}
      >
        <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {t('detail.loading')}
        </div>
      </div>
    );
  }

  if (errorCode === 'not-found' || !timelineData) {
    return (
      <FullScreenMessage
        title={t('detail.notFound')}
        description={error || t('detail.notFoundDesc')}
        actionLabel={t('nav.reload', { ns: 'common' })}
        onAction={retry}
      />
    );
  }

  return (
    <div className="flex h-screen flex-col" style={{ backgroundColor: 'var(--color-bg-app)' }}>
      <div className="flex-1 overflow-y-auto">
        <TimelineCalendar
          key={id ?? 'timeline-detail'}
          data={timelineData}
          isReadOnly={false}
          showControls
          showPeriodHeader
          initialScale={pendingInitialScale}
          initialViewRange={savedViewRange}
          onViewChange={handleViewChange}
          onSectionLabelChange={handleSectionLabelChange}
          onSectionDelete={handleSectionDelete}
          onSectionMove={handleSectionMove}
          onSectionVisibilityToggle={handleSectionVisibilityToggle}
          onItemTitleChange={handleItemTitleChange}
          onItemCreate={handleItemCreate}
          onItemDateRangeChange={handleItemDateRangeChange}
          onItemDelete={handleItemDelete}
          headerContent={(
            <TimelineHeader
              fileName={fileName}
              onAddTimeline={addTimelineSection}
              onSave={handleSave}
              onSaveAs={handleSaveAs}
              onNewFile={handleNewFile}
              onOpenFile={handleOpenFile}
            />
          )}
        />
      </div>
    </div>
  );
};
